import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  Injector,
  OnChanges,
  OnInit,
  SimpleChange,
  Type,
  ViewContainerRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReplaySubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { CMSFacade } from 'ish-core/facades/cms.facade';
import { ContentPageletView } from 'ish-core/models/content-view/content-view.model';
import { whenTruthy } from 'ish-core/utils/operators';
import { ContentDesignViewWrapperComponent } from 'ish-shared/cms/components/content-design-view-wrapper/content-design-view-wrapper.component';
import { CMSComponentProvider, CMS_COMPONENT } from 'ish-shared/cms/configurations/injection-keys';
import { CMSComponent } from 'ish-shared/cms/models/cms-component/cms-component.model';

/**
 * The Content Pagelet Component renders the pagelet for the given 'pageletId'.
 * For the rendering an Angular component is used that has to be provided
 * for the DefinitionQualifiedName of the pagelet.
 *
 * @example
 * <ish-content-pagelet [pageletId]="pagelet"></ish-content-pagelet>
 */
@Component({
  selector: 'ish-content-pagelet',
  templateUrl: './content-pagelet.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ContentDesignViewWrapperComponent],
})
export class ContentPageletComponent implements OnChanges, OnInit {
  private injector = inject(Injector);
  private cmsFacade = inject(CMSFacade);
  private cdRef = inject(ChangeDetectorRef);

  /**
   * The Id of the Pagelet that is to be rendered.
   */
  readonly pageletId = input<string>(undefined);

  readonly cmsOutlet = viewChild('cmsOutlet', { read: ViewContainerRef });

  private pageletId$ = new ReplaySubject<string>(1);
  private destroyRef = inject(DestroyRef);
  ngOnInit() {
    this.pageletId$
      .pipe(
        switchMap(pageletId => this.cmsFacade.pagelet$(pageletId)),
        whenTruthy(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(pagelet => {
        this.mapComponent(pagelet);
        this.cdRef.markForCheck();
      });
  }

  ngOnChanges() {
    this.pageletId$.next(this.pageletId());
  }

  private mapComponent(pagelet: ContentPageletView) {
    const components = this.injector.get<CMSComponentProvider[]>(CMS_COMPONENT, []);
    const mappedComponent = components.find(c => c.definitionQualifiedName === pagelet.definitionQualifiedName);

    if (mappedComponent) {
      // const componentRef = this.createComponent(mappedComponent);
      // this.initializeComponent(componentRef, pagelet);
      mappedComponent.loadComponent().then(component => {
        const componentRef = this.createComponent(component);
        this.initializeComponent(componentRef, pagelet);
      });
    } else {
      console.warn(`did not find mapping for ${pagelet.id} (${pagelet.definitionQualifiedName})`);
    }
  }

  private createComponent(cmsComponent: Type<CMSComponent>) {
    this.cmsOutlet().clear();
    return this.cmsOutlet().createComponent(cmsComponent);
  }

  private initializeComponent(instanceRef: ComponentRef<CMSComponent>, pagelet: ContentPageletView) {
    instanceRef.setInput('pagelet', pagelet);

    // OnChanges has to be manually invoked on dynamically created components
    if (instanceRef.instance.ngOnChanges) {
      instanceRef.instance.ngOnChanges({ pagelet: new SimpleChange(undefined, pagelet, true) });
    }
  }
}
